(async () => {
    const { Core } = await importModule('Core');
    const {
        $fetch,
        debounce,
        $toggleDisplay,
        $replaceContent,
        scrollLock
    } = await importModule('Utils');

    const ATTR_ONLY_STOCK = 'only-stock-products';
    const ATTR_MIN_CHARS = 'min-chars';
    const ATTR_LIMIT = 'results-limit';
    const ATTR_WITH_SUGGESTIONS = 'with-suggestions';
    const ATTR_WITH_RECENT = 'with-recent';
    const ATTR_SOURCES = 'sources';

    const RQ_SECTION = 'r_predictive-search';
    const ATTR_NO_RESULTS = 'data-empty-results';

    const DEFAULT_MIN_CHARS = 3;
    const DEFAULT_LIMIT = 5;

    /*
     * Shopify Predictive Search searchable fields.
     *
     * By default Shopify searches:
     * - title
     * - product_type
     * - variants.title
     * - vendor
     *
     * We explicitly add:
     * - variants.sku
     */
    const SEARCH_FIELDS = [
        'title',
        'product_type',
        'variants.title',
        'variants.sku',
        'body',
        'tag',
        'variants.barcode',
        'vendor'
    ].join(',');

    const CN_ACTIVE = '!active';
    const CN_LOADING = '!loading';

    customElements.define(
        'predictive-search',

        class extends Core {

            elements = {
                form: 'form',
                input: 'input[name="q"]',
                button: '[data-element="button"]',
                results: '[data-results]',
                wrapper: '[data-wrapper]',
                initial: '[data-initial]',
                suggestions: '*[data-suggestion]'
            };

            subscriptions = {
                'recentSearches:select': '_updateInputValue'
            };

            cache = {};

            render() {

                // TEMP: dirty lights off fix
                this.addEventListener('click', (e) => {
                    this.$input.focus();
                });

                this._disableAutocomplete();
                this._handleInput();
                this._handleButtonClick();
                this._handleOuterClick();
                this._handleSuggestions();
                this._handleRecentSearches();
            }

            /* ----------------------------------------
             * INPUT
             * ------------------------------------- */

            _disableAutocomplete() {
                this.$input.setAttribute('autocomplete', 'off');
            }

            _handleInput() {

                this.$input.addEventListener(
                    'focus',
                    this._handleFocus.bind(this)
                );

                this.$input.addEventListener(
                    'blur',
                    this._handleBlur.bind(this)
                );

                this.$input.addEventListener(
                    'input',
                    debounce(
                        this._inputHandler.bind(this),
                        300
                    ).bind(this)
                );
            }

            /* ----------------------------------------
             * SEARCH BUTTON
             * ------------------------------------- */

            _handleButtonClick() {
                this.$button.addEventListener(
                    'click',
                    this._handleButtonOnEmpty.bind(this)
                );
            }

            _handleButtonOnEmpty(e) {

                if (this.$input.value) {
                    return;
                }

                e.preventDefault();

                this.$input.focus();
            }

            /* ----------------------------------------
             * FOCUS / BLUR
             * ------------------------------------- */

            _handleFocus() {
                if (this.dataset.partsModule && this.sources.split(',').includes('product')) {
                    import(this.dataset.partsModule).then((module) => module.loadIndex(this.dataset.partsIndex)).catch(() => {});
                }

                this._handleActiveState();

                this.lights = false;
            }

            _handleBlur() {

                this.lights = true;
            }

            _handleActiveState() {

                if (this.isEmptyResults) {

                    this.showInitial = false;
                    this.active = true;

                    return;
                }

                this.showInitial = true;

                this.active =
                    !!this.minQueryChars ||
                    !!this.$initial;
            }

            /* ----------------------------------------
             * SEARCH HANDLER
             * ------------------------------------- */

            async _inputHandler() {
                const request = this.searchRequest = (this.searchRequest || 0) + 1;

                /*
                 * Don't search until minimum characters
                 * have been entered.
                 */
                if (!this.minQueryChars) {

                    this.loading = false;
                    this.showResults = false;
                    this.isEmptyResults = false;

                    this._handleActiveState();

                    return;
                }

                this.loading = true;

                try {

                    await this._getQueryResults();

                    if (request === this.searchRequest) this._renderQueryResults();

                } catch (error) {

                    console.error(
                        '[Predictive Search]',
                        error
                    );

                    if (request === this.searchRequest) {
                        this.loading = false;
                        this.showResults = false;
                    }
                }
            }

            /* ----------------------------------------
             * FETCH SEARCH RESULTS
             * ------------------------------------- */

            async _getQueryResults() {
                const query = this.query;
                const cacheKey = query.toLowerCase();
                if (this.cache[cacheKey] && Date.now() - this.cache[cacheKey].time < 60000) return;
                const includeParts = this.dataset.partsModule && this.sources.split(',').includes('product');
                const moduleTask = includeParts
                    ? import(this.dataset.partsModule).then(async (module) => {
                        await module.loadIndex(this.dataset.partsIndex);
                        return module;
                    }).catch(() => null)
                    : Promise.resolve(null);
                const native = await this._fetchResultsDoc(query);
                if (!native) throw new Error('Search unavailable');
                if (includeParts && native.hasAttribute(ATTR_NO_RESULTS)) {
                    native.content.querySelector('.\\#predictive-search-results-no-results')?.setAttribute('hidden', '');
                    const status = document.createElement('p');
                    status.dataset.partsLoading = '';
                    status.setAttribute('role', 'status');
                    status.textContent = this.dataset.partsLoading;
                    native.content.append(status);
                }
                this.cache[cacheKey] = { time: Date.now(), empty: native.hasAttribute(ATTR_NO_RESULTS), content: native };
                if (!includeParts) return;
                moduleTask.then(async (module) => {
                    let content = native;
                    try {
                        if (module) content = await module.addPredictiveMatches(native, query, {
                            indexUrl: this.dataset.partsIndex,
                            onlyStock: this.hasAttribute(ATTR_ONLY_STOCK),
                            limit: this.limit,
                            heading: this.dataset.partsHeading,
                            viewAll: this.dataset.partsViewAll,
                            searchUrl: this.dataset.partsSearchUrl
                        });
                    } finally {
                        content.content.querySelector('[data-parts-loading]')?.remove();
                        content.content.querySelector('.\\#predictive-search-results-no-results')?.removeAttribute('hidden');
                        this.cache[cacheKey] = { time: Date.now(), empty: content.hasAttribute(ATTR_NO_RESULTS), content };
                        if (this.query.toLowerCase() === cacheKey && this.minQueryChars && this.active) this._renderQueryResults();
                    }
                }).catch(() => {});
            }

            async _fetchResultsDoc(query) {
                const params = new URLSearchParams(this.searchParams);
                params.set('q', query);
                return $fetch(`${routes.predictive_search_url}?${params}`, { select: '[data-template]' });
            }

            /* ----------------------------------------
             * RENDER RESULTS
             * ------------------------------------- */

            _renderQueryResults() {

                const cacheKey =
                    this.query.toLowerCase();

                const result =
                    this.cache[cacheKey];

                if (!result) {
                    return;
                }

                $replaceContent(
                    this.$results,
                    result.content
                );

                const isEmpty =
                    result.empty;

                this.isEmptyResults = !isEmpty;

                this._handleActiveState();

                this.loading = false;

                this.showResults = true;
            }

            /* ----------------------------------------
             * OUTSIDE CLICK
             * ------------------------------------- */

            _handleOuterClick() {

                document.addEventListener(
                    'click',
                    (e) => {

                        if (
                            !this.contains(e.target) &&
                            this.active
                        ) {
                            this.active = false;
                        }
                    }
                );
            }

            /* ----------------------------------------
             * SUGGESTIONS
             * ------------------------------------- */

            _handleSuggestions() {

                if (!this.withSuggestions) {
                    return;
                }

                this.$suggestions.map(
                    ($suggestion) => {

                        $suggestion.addEventListener(
                            'click',
                            (e) => {

                                e.preventDefault();

                                this.$input.value =
                                    e.currentTarget.dataset.suggestion;

                                this.$input.focus();

                                this.$input.dispatchEvent(
                                    new Event('input')
                                );
                            }
                        );
                    }
                );
            }

            /* ----------------------------------------
             * RECENT SEARCHES
             * ------------------------------------- */

            _handleRecentSearches() {

                if (!this.withRecent) {
                    return;
                }

                this.subscribe(
                    'recentSearches:select'
                );

                this.addEventListener(
                    'click',
                    (e) => {

                        if (e.target.href) {
                            this._publishQuery(
                                this.query
                            );
                        }
                    }
                );

                this.$form.addEventListener(
                    'submit',
                    (e) => {

                        e.preventDefault();

                        const query =
                            new FormData(this.$form)
                                .get('q');

                        this._publishQuery(query);

                        this.$form.submit();
                    },
                    {
                        once: true
                    }
                );
            }

            _updateInputValue({ value }) {

                this.$input.value = value;

                this.$input.focus();
            }

            _publishQuery(query) {

                this.publish(
                    'search:query',
                    {
                        query
                    }
                );
            }

            /* ----------------------------------------
             * STATE
             * ------------------------------------- */

            set loading(state) {

                this.classList.toggle(
                    CN_LOADING,
                    state
                );
            }

            set showInitial(state) {

                if (this.$initial) {

                    $toggleDisplay(
                        this.$initial,
                        state
                    );
                }
            }

            set showResults(state) {

                $toggleDisplay(
                    this.$results,
                    state
                );
            }

            set active(state) {

                this.$wrapper
                    .classList
                    .toggle(
                        CN_ACTIVE,
                        state
                    );

                scrollLock(state);

                this._active = state;
            }

            get active() {

                return !!this._active;
            }

            get lights() {

                return this._lights;
            }

            set lights(state) {

                this.publish(
                    'lights-out',
                    !state
                );

                this._lights = state;
            }

            /* ----------------------------------------
             * QUERY
             * ------------------------------------- */

            get minQueryChars() {

                return (
                    this.query.length >=
                    this.minChars
                );
            }

            get query() {

                return this.$input.value.trim();
            }

            get minChars() {

                return this.hasAttribute(
                    ATTR_MIN_CHARS
                )
                    ? Number(
                        this.getAttribute(
                            ATTR_MIN_CHARS
                        )
                    )
                    : DEFAULT_MIN_CHARS;
            }

            /* ----------------------------------------
             * SHOPIFY SEARCH PARAMS
             * ------------------------------------- */

            get searchParams() {

                return new URLSearchParams({

                    /*
                     * User search query.
                     *
                     * This query will now be matched
                     * against SKU as well.
                     */
                    q: this.query,

                    /*
                     * Resource types.
                     */
                    'resources[type]':
                        this.sources,

                    /*
                     * Inventory behavior.
                     */
                    'resources[options][unavailable_products]':
                        this.onlyStock,

                    /*
                     * IMPORTANT:
                     *
                     * Add variants.sku here.
                     */
                    'resources[options][fields]':
                        SEARCH_FIELDS,

                    /*
                     * Number of results.
                     */
                    'resources[limit]':
                        this.limit,

                    /*
                     * Shopify section.
                     */
                    section_id:
                        RQ_SECTION

                }).toString();
            }

            /* ----------------------------------------
             * SEARCH SOURCES
             * ------------------------------------- */

            get sources() {

                return this.hasAttribute(
                    ATTR_SOURCES
                )
                    ? this
                        .getAttribute(ATTR_SOURCES)
                        .split(',')
                        .filter(Boolean)
                        .join(',')
                    : '';
            }

            /* ----------------------------------------
             * STOCK
             * ------------------------------------- */

            get onlyStock() {

                return this.hasAttribute(
                    ATTR_ONLY_STOCK
                )
                    ? 'hide'
                    : 'last';
            }

            /* ----------------------------------------
             * RESULT LIMIT
             * ------------------------------------- */

            get limit() {

                return this.hasAttribute(
                    ATTR_LIMIT
                )
                    ? this.getAttribute(
                        ATTR_LIMIT
                    )
                    : DEFAULT_LIMIT;
            }

            /* ----------------------------------------
             * SUGGESTIONS
             * ------------------------------------- */

            get withSuggestions() {

                return this.hasAttribute(
                    ATTR_WITH_SUGGESTIONS
                );
            }

            /* ----------------------------------------
             * RECENT SEARCH
             * ------------------------------------- */

            get withRecent() {

                return this.hasAttribute(
                    ATTR_WITH_RECENT
                );
            }
        }
    );
})();
